<template>
  <div class="ie-wrap" :class="{ 'ie-wrap--loading': isLoading }">
    <!-- Compact app bar: back, title-as-doc-name, status, actions -->
    <header class="ie-appbar">
      <div class="ie-appbar__left">
        <button type="button" class="ie-icon-btn" title="Back to Info Center" @click="cancel">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div class="ie-appbar__name">
          <span class="ie-appbar__label">Document</span>
          <span class="ie-appbar__title">{{ form.title || 'Untitled document' }}</span>
        </div>
      </div>
      <div class="ie-appbar__right">
        <span class="ie-status-chip" :class="statusClass">
          <span class="ie-status-dot"></span>
          <span>{{ statusLabel }}</span>
        </span>
        <button
          type="button"
          class="ie-btn ie-btn--ghost"
          :disabled="isSaving || !canSave"
          :title="saveDisabledReason"
          @click="saveDraft"
        >{{ saveDraftLabel }}</button>
        <button
          type="button"
          class="ie-btn ie-btn--primary"
          :disabled="isSaving || !canSave"
          :title="saveDisabledReason"
          @click="openPublishModal"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          {{ publishLabel }}
        </button>
      </div>
    </header>

    <!-- Word-style ribbon: full-width strip just under the app bar.
         RichTextToolbar binds to the TipTap Editor instance exposed by
         RichTextEditor via the editor-ready event. -->
    <div v-if="!isLoading" class="ie-ribbon">
      <div class="ie-ribbon__inner">
        <RichTextToolbar :editor="editorInstance" />
      </div>
    </div>

    <CAlert v-if="errorMessage" color="danger" show class="ie-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="ie-alert">{{ successMessage }}</CAlert>

    <div v-if="isLoading" class="ie-loading">Loading…</div>

    <!-- Word-like canvas: gray backdrop, centered white page with TipTap inside. -->
    <div v-else class="ie-canvas">
      <article class="ie-page">
        <input
          v-model="form.title"
          type="text"
          class="ie-doc-title"
          placeholder="Document title"
          maxlength="255"
        />
        <input
          v-model="form.summary"
          type="text"
          class="ie-doc-summary"
          placeholder="Add a one-line summary (optional)"
          maxlength="500"
        />

        <div class="ie-doc-meta">
          <span class="ie-doc-meta__chip" :class="categoryPillClass">
            {{ categoryIcon }} {{ categoryLabel }}
          </span>
          <span v-if="form.pinned" class="ie-doc-meta__chip ie-doc-meta__chip--pin">★ Pinned</span>
          <span v-if="audienceSummary" class="ie-doc-meta__chip ie-doc-meta__chip--soft">{{ audienceSummary }}</span>
          <span v-if="orgSummary" class="ie-doc-meta__chip ie-doc-meta__chip--soft">{{ orgSummary }}</span>
        </div>

        <hr class="ie-doc-divider" />

        <RichTextEditor
          ref="editor"
          :value="form.body_html"
          :json="form.body_json"
          placeholder="Start writing…"
          @input="onBodyInput"
          @update:json="onBodyJson"
          @editor-ready="onEditorReady"
        />
      </article>

      <div v-if="isEdit" class="ie-canvas__footer">
        <button type="button" class="ie-btn ie-btn--danger" @click="confirmDelete">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
          Delete document
        </button>
      </div>
    </div>

    <!-- Publish modal — opens on Publish click; collects audience/category. -->
    <transition name="ie-modal-fade">
      <div
        v-if="showPublishModal"
        class="ie-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="modalTitleId"
        @mousedown.self="closePublishModal"
      >
        <div class="ie-modal__panel" role="document" @mousedown.stop>
          <!-- Decorative gradient header with a live document preview -->
          <header class="ie-modal__head">
            <div class="ie-modal__head-row">
              <div class="ie-modal__icon-tile" :style="{ background: categoryGradient }">
                <span>{{ form.icon || categoryMeta.icon }}</span>
              </div>
              <div class="ie-modal__head-text">
                <h2 :id="modalTitleId">{{ originalStatus === 'published' ? 'Update published document' : 'Publish document' }}</h2>
                <p>{{ form.title || 'Untitled document' }}</p>
              </div>
              <button type="button" class="ie-icon-btn ie-icon-btn--close" @click="closePublishModal" aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="ie-modal__progress">
              <span class="ie-modal__progress-step is-done"><span class="ie-modal__progress-dot"></span>Drafted</span>
              <span class="ie-modal__progress-line"></span>
              <span class="ie-modal__progress-step is-current"><span class="ie-modal__progress-dot"></span>Set audience</span>
              <span class="ie-modal__progress-line"></span>
              <span class="ie-modal__progress-step"><span class="ie-modal__progress-dot"></span>Live</span>
            </div>
          </header>

          <div class="ie-modal__body">
            <section class="ie-modal__section">
              <div class="ie-modal__h-row">
                <span class="ie-modal__h-icon" aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                </span>
                <h3 class="ie-modal__h">Category</h3>
                <span class="ie-modal__h-meta">{{ categoryMeta.label }}</span>
              </div>
              <div class="ie-cat-grid">
                <button
                  v-for="cat in categoryOptions"
                  :key="cat.key"
                  type="button"
                  class="ie-cat-btn"
                  :class="{ 'is-active': form.category === cat.key }"
                  @click="form.category = cat.key"
                >
                  <span class="ie-cat-btn__icon">{{ cat.icon }}</span>
                  <span class="ie-cat-btn__label">{{ cat.label }}</span>
                </button>
              </div>
            </section>

            <section class="ie-modal__section">
              <div class="ie-modal__h-row">
                <span class="ie-modal__h-icon" aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </span>
                <h3 class="ie-modal__h">Audience · Roles</h3>
                <span class="ie-modal__h-meta">{{ audienceSummary }}</span>
              </div>
              <p class="ie-modal__hint">Pick which roles should see this document. Leave empty (or include “Everyone”) to make it visible to all staff.</p>
              <div class="ie-checklist">
                <label class="ie-aud-row ie-aud-row--master">
                  <input type="checkbox" :checked="audienceHasAll" @change="toggleAll" />
                  <span class="ie-aud-label">Everyone</span>
                </label>
                <label
                  v-for="role in roleOptions"
                  :key="role"
                  class="ie-aud-row"
                  :class="{ 'is-muted': audienceHasAll }"
                >
                  <input
                    type="checkbox"
                    :checked="form.audience_roles.includes(role) && !audienceHasAll"
                    @change="toggleRole(role)"
                  />
                  <span class="ie-aud-label">{{ formatRole(role) }}</span>
                </label>
              </div>
            </section>

            <section class="ie-modal__section">
              <div class="ie-modal__h-row">
                <span class="ie-modal__h-icon" aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21V7l9-4 9 4v14"/><path d="M9 21V11h6v10"/></svg>
                </span>
                <h3 class="ie-modal__h">Audience · Organisations</h3>
                <span class="ie-modal__h-meta">{{ orgSummary || 'All organisations' }}</span>
              </div>
              <p class="ie-modal__hint">Restrict to certain organisations, or leave empty for global access.</p>
              <div class="ie-checklist">
                <label class="ie-aud-row ie-aud-row--master">
                  <input type="checkbox" :checked="orgHasAll" @change="toggleAllOrgs" />
                  <span class="ie-aud-label">All organisations</span>
                </label>
                <label
                  v-for="org in organisationOptions"
                  :key="org.id"
                  class="ie-aud-row"
                  :class="{ 'is-muted': orgHasAll }"
                >
                  <input
                    type="checkbox"
                    :checked="form.audience_organisations.includes(org.id) && !orgHasAll"
                    @change="toggleOrg(org.id)"
                  />
                  <span class="ie-aud-label">{{ org.name }}</span>
                </label>
                <p v-if="!organisationOptions.length" class="ie-modal__hint">No organisations loaded yet.</p>
              </div>
            </section>

            <section class="ie-modal__section ie-modal__section--inline">
              <div class="ie-inline-field">
                <label class="ie-modal__h ie-modal__h--inline" for="ie-modal-icon">Icon</label>
                <input
                  id="ie-modal-icon"
                  v-model="form.icon"
                  type="text"
                  class="ie-modal__icon-input"
                  placeholder="📋"
                  maxlength="8"
                />
              </div>
              <label class="ie-aud-row ie-aud-row--inline">
                <input type="checkbox" v-model="form.pinned" />
                <span class="ie-aud-label">Pin to top of the Info Center</span>
              </label>
            </section>
          </div>

          <footer class="ie-modal__foot">
            <div class="ie-modal__ready">
              <span class="ie-modal__ready-dot" :class="{ 'is-ok': canSave }"></span>
              <span>{{ canSave ? 'Ready to publish' : (saveDisabledReason || 'Almost there') }}</span>
            </div>
            <div class="ie-modal__foot-actions">
              <button type="button" class="ie-btn ie-btn--ghost" :disabled="isSaving" @click="closePublishModal">Cancel</button>
              <button type="button" class="ie-btn ie-btn--ghost" :disabled="isSaving" @click="submitFromModal('draft')">
                Save as Draft
              </button>
              <button
                type="button"
                class="ie-btn ie-btn--primary"
                :disabled="isSaving || !canSave"
                @click="submitFromModal('published')"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                {{ isSaving ? 'Publishing…' : (originalStatus === 'published' ? 'Save & Publish' : 'Publish now') }}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </transition>

    <AppConfirmDialog
      :visible="showDeleteConfirm"
      title="Delete this document?"
      :message="deleteMessage"
      confirm-label="Delete"
      variant="danger"
      @cancel="showDeleteConfirm = false"
      @confirm="deleteDocument"
    />

    <!-- Full-screen success overlay shown briefly between publish-success
         and the navigation back to /info-center. The visual confirms the
         action landed before the listing reloads. -->
    <transition name="ie-success-fade">
      <div v-if="showSuccessAnimation" class="ie-success-overlay">
        <div class="ie-success-card">
          <svg class="ie-success-check" viewBox="0 0 80 80" aria-hidden="true">
            <circle class="ie-success-check__ring" cx="40" cy="40" r="34" />
            <path class="ie-success-check__tick" d="M24 42l11 11 22-25" />
          </svg>
          <h2>{{ successHeading }}</h2>
          <p>{{ successCopy }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import RichTextEditor from '@/components/RichTextEditor.vue'
import RichTextToolbar from '@/components/RichTextToolbar.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'

const CATEGORY_OPTIONS = [
  { key: 'policy',       label: 'Policy',       icon: '📋', pill: 'ie-pill--green',  gradient: 'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)' },
  { key: 'sop',          label: 'SOP',          icon: '⚙️', pill: 'ie-pill--blue',   gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' },
  { key: 'guide',        label: 'Guide',        icon: '📘', pill: 'ie-pill--violet', gradient: 'linear-gradient(135deg, #6d28d9 0%, #a78bfa 100%)' },
  { key: 'announcement', label: 'Announcement', icon: '📣', pill: 'ie-pill--amber',  gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)' },
  { key: 'faq',          label: 'FAQ',          icon: '❓', pill: 'ie-pill--teal',   gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)' },
  { key: 'training',     label: 'Training',     icon: '🎓', pill: 'ie-pill--pink',   gradient: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)' },
  { key: 'compliance',   label: 'Compliance',   icon: '🛡️', pill: 'ie-pill--red',    gradient: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)' },
  { key: 'benefits',     label: 'Benefits',     icon: '💼', pill: 'ie-pill--cyan',   gradient: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)' }
]

let modalCounter = 0

function blankForm () {
  return {
    title: '',
    summary: '',
    body_html: '',
    body_json: null,
    category: 'policy',
    audience_roles: [],
    audience_organisations: [],
    status: 'draft',
    icon: '',
    pinned: false
  }
}

export default {
  name: 'InfoEditor',
  components: { RichTextEditor, RichTextToolbar, AppConfirmDialog },
  data () {
    modalCounter += 1
    return {
      form: blankForm(),
      categoryOptions: CATEGORY_OPTIONS,
      roleOptions: [],
      organisationOptions: [],
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      showDeleteConfirm: false,
      showPublishModal: false,
      showSuccessAnimation: false,
      successHeading: 'Published!',
      successCopy: 'Document is live in the Info Center.',
      docId: null,
      originalStatus: 'draft',
      modalTitleId: `ie-modal-title-${modalCounter}`,
      // Captured from the RichTextEditor's editor-ready event so the
      // sibling RichTextToolbar can bind to the same TipTap instance.
      editorInstance: null,
      // Explicit "scope" toggle for the org picker. Without this the
      // checkbox state was derived from an empty list, which trapped
      // the user in the "all" state — unchecking the master did
      // nothing because the specific rows stayed disabled. 'all' /
      // 'specific'. Re-derived from the saved doc on load.
      orgScope: 'all'
    }
  },
  computed: {
    isEdit () {
      return Boolean(this.$route.params.id)
    },
    canSave () {
      return Boolean(this.form.title && this.form.title.trim())
    },
    saveDisabledReason () {
      if (this.isSaving) return ''
      if (!this.form.title || !this.form.title.trim()) return 'Add a title first.'
      return ''
    },
    audienceHasAll () {
      return this.form.audience_roles.includes('all')
    },
    orgHasAll () {
      return this.orgScope === 'all'
    },
    statusLabel () {
      const map = { draft: 'Draft', published: 'Published', archived: 'Archived' }
      return map[this.form.status] || 'Draft'
    },
    statusClass () {
      return {
        'is-draft': this.form.status === 'draft',
        'is-published': this.form.status === 'published',
        'is-archived': this.form.status === 'archived'
      }
    },
    saveDraftLabel () {
      if (this.isSaving && this.showPublishModal === false && this.form.status !== 'published') return 'Saving…'
      return this.isEdit && this.originalStatus !== 'published' ? 'Save Draft' : 'Save as Draft'
    },
    publishLabel () {
      return this.originalStatus === 'published' ? 'Republish…' : 'Publish…'
    },
    categoryMeta () {
      return CATEGORY_OPTIONS.find((c) => c.key === this.form.category) || CATEGORY_OPTIONS[0]
    },
    categoryLabel () {
      return this.categoryMeta.label
    },
    categoryIcon () {
      return this.form.icon || this.categoryMeta.icon
    },
    categoryPillClass () {
      return this.categoryMeta.pill
    },
    categoryGradient () {
      return this.categoryMeta.gradient || 'linear-gradient(135deg, #475569 0%, #64748b 100%)'
    },
    audienceSummary () {
      const roles = this.form.audience_roles
      if (!roles.length || roles.includes('all')) return 'For everyone'
      if (roles.length === 1) return `For ${this.formatRole(roles[0])}`
      return `For ${roles.length} roles`
    },
    orgSummary () {
      const ids = this.form.audience_organisations
      if (!ids.length) return ''
      if (ids.length === 1) {
        const o = this.organisationOptions.find((o) => o.id === ids[0])
        return o ? `In ${o.name}` : ''
      }
      return `In ${ids.length} organisations`
    },
    deleteMessage () {
      return `“${this.form.title}” will be permanently removed.`
    }
  },
  created () {
    this.docId = this.$route.params.id ? Number(this.$route.params.id) : null
    this.loadAudienceOptions()
    if (this.docId) {
      this.loadDocument()
    }
  },
  beforeRouteLeave (to, from, next) {
    next()
  },
  methods: {
    loadAudienceOptions () {
      axios.get(`${this.$apiAdress}/api/info-documents/audience-options`)
        .then((response) => {
          const data = response.data || {}
          if (Array.isArray(data.roles)) {
            this.roleOptions = data.roles.filter((r) => r !== 'all')
          }
          if (Array.isArray(data.categories) && data.categories.length) {
            this.categoryOptions = data.categories.map((key) => {
              const existing = CATEGORY_OPTIONS.find((c) => c.key === key)
              return existing || { key, label: key.charAt(0).toUpperCase() + key.slice(1), icon: '📄', pill: 'ie-pill--gray' }
            })
          }
          if (Array.isArray(data.organisations)) {
            this.organisationOptions = data.organisations
              .map((o) => ({ id: Number(o.id), name: o.name || `Org ${o.id}` }))
              .filter((o) => o.id)
          }
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            this.errorMessage = 'You do not have permission to edit Info Center documents.'
            setTimeout(() => this.$router.replace('/info-center'), 1200)
          }
        })
    },
    loadDocument () {
      this.isLoading = true
      axios.get(`${this.$apiAdress}/api/info-documents/${this.docId}`)
        .then((response) => {
          const d = response.data || {}
          this.form = {
            title: d.title || '',
            summary: d.summary || '',
            body_html: d.body_html || '',
            body_json: d.body_json ? this.safeParseJson(d.body_json) : null,
            category: d.category || 'policy',
            audience_roles: Array.isArray(d.audience_roles) ? [...d.audience_roles] : [],
            audience_organisations: Array.isArray(d.audience_organisations) ? d.audience_organisations.map((id) => Number(id)) : [],
            status: d.status || 'draft',
            icon: d.icon || '',
            pinned: Boolean(d.pinned)
          }
          this.originalStatus = d.status || 'draft'
          this.orgScope = this.form.audience_organisations.length ? 'specific' : 'all'
        })
        .catch((error) => {
          this.errorMessage = this.formatRequestError(error, 'Unable to load document.')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    safeParseJson (raw) {
      if (raw && typeof raw === 'object') return raw
      try { return JSON.parse(raw) } catch (_) { return null }
    },
    onBodyInput (html) {
      this.form.body_html = html
    },
    onBodyJson (json) {
      this.form.body_json = json
    },
    onEditorReady (editor) {
      // Stash the TipTap Editor instance so RichTextToolbar can bind to
      // the same instance from outside the editor's component tree.
      // The child also emits a null payload on its beforeDestroy hook.
      this.editorInstance = editor
    },
    toggleAll (event) {
      if (event.target.checked) {
        this.form.audience_roles = ['all']
      } else {
        this.form.audience_roles = this.form.audience_roles.filter((r) => r !== 'all')
      }
    },
    toggleRole (role) {
      // Picking a specific role implicitly leaves the "Everyone" state
      // — mirrors how toggleOrg flips orgScope to 'specific'. Strip the
      // 'all' sentinel before toggling so the master checkbox visibly
      // unticks itself the moment the user picks anything specific.
      const next = this.form.audience_roles.filter((r) => r !== 'all')
      const idx = next.indexOf(role)
      if (idx === -1) {
        next.push(role)
      } else {
        next.splice(idx, 1)
      }
      this.form.audience_roles = next
    },
    toggleAllOrgs (event) {
      // Master toggle for the org picker. Checking goes back to "all"
      // mode (empty list, server interprets as visible everywhere).
      // Unchecking flips into manual mode so the specific-org rows
      // become editable — even before the user has actually picked one.
      if (event.target.checked) {
        this.orgScope = 'all'
        this.form.audience_organisations = []
      } else {
        this.orgScope = 'specific'
      }
    },
    toggleOrg (orgId) {
      // Picking a specific org implicitly leaves "all" mode. We also
      // flip the scope when the user clicks a row directly (e.g. from
      // a fresh modal open) so the affordance feels obvious.
      this.orgScope = 'specific'
      const id = Number(orgId)
      const idx = this.form.audience_organisations.indexOf(id)
      if (idx === -1) {
        this.form.audience_organisations.push(id)
      } else {
        this.form.audience_organisations.splice(idx, 1)
      }
    },
    formatRole (role) {
      if (!role) return ''
      return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    saveDraft () {
      this.submit('draft')
    },
    openPublishModal () {
      if (!this.canSave) return
      this.showPublishModal = true
      this.errorMessage = ''
    },
    closePublishModal () {
      if (this.isSaving) return
      this.showPublishModal = false
    },
    submitFromModal (targetStatus) {
      this.submit(targetStatus, () => {
        this.showPublishModal = false
      })
    },
    submit (targetStatus, onSuccess) {
      if (!this.canSave || this.isSaving) return
      this.isSaving = true
      this.errorMessage = ''
      this.successMessage = ''

      // Read the live editor state via the ref so we never submit stale
      // body content if the parent prop hasn't synced yet.
      const editorRef = this.$refs.editor
      const liveHtml = editorRef && typeof editorRef.getHTML === 'function' ? editorRef.getHTML() : null
      const liveJson = editorRef && typeof editorRef.getJSON === 'function' ? editorRef.getJSON() : null
      const bodyHtml = liveHtml != null ? liveHtml : (this.form.body_html || '')
      const bodyJson = liveJson != null ? liveJson : (this.form.body_json || null)

      const payload = {
        title: this.form.title.trim(),
        summary: this.form.summary ? this.form.summary.trim() : null,
        body_html: bodyHtml,
        body_json: bodyJson,
        category: this.form.category,
        audience_roles: this.form.audience_roles,
        audience_organisations: this.form.audience_organisations,
        status: targetStatus,
        icon: this.form.icon ? this.form.icon.trim() : null,
        pinned: Boolean(this.form.pinned)
      }

      const request = this.isEdit
        ? axios.put(`${this.$apiAdress}/api/info-documents/${this.docId}`, payload)
        : axios.post(`${this.$apiAdress}/api/info-documents`, payload)

      request
        .then((response) => {
          const saved = response.data || {}
          this.form.status = saved.status || targetStatus
          this.originalStatus = saved.status || targetStatus
          if (typeof onSuccess === 'function') onSuccess()

          if (targetStatus === 'published') {
            // Celebrate the publish, then bounce to the listing so the
            // user immediately sees the doc live alongside everything
            // else. InfoCenter's activated() hook refetches the list.
            this.successHeading = 'Published!'
            this.successCopy = '“' + (this.form.title || 'Document') + '” is live in the Info Center.'
            this.showSuccessAnimation = true
            setTimeout(() => {
              this.$router.push({ path: '/info-center', query: { refresh: Date.now() } })
            }, 1400)
            return
          }

          // Draft / archive — stay on the editor and surface a quick
          // banner. Promote a brand-new draft into edit mode so further
          // saves PUT instead of re-POSTing.
          this.successMessage = 'Draft saved.'
          if (!this.isEdit && saved.id) {
            this.docId = saved.id
            this.$router.replace({ path: `/info-center/${saved.id}/edit` })
          }
          setTimeout(() => { this.successMessage = '' }, 2500)
        })
        .catch((error) => {
          this.errorMessage = this.formatRequestError(error, 'Unable to save document.')
          // eslint-disable-next-line no-console
          console.error('[InfoEditor] save failed', error?.response || error)
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    formatRequestError (error, fallback) {
      if (!error) return fallback
      if (!error.response) {
        return `Could not reach the server. ${error.message || 'Check your connection.'}`
      }
      const { status, data } = error.response
      const errBag = (data && (data.error || data.errors))
      if (errBag && typeof errBag === 'object') {
        const first = Object.values(errBag)[0]
        return Array.isArray(first) ? first[0] : String(first)
      }
      if (data && data.message) {
        if (status === 500) return `Server error: ${data.message}. Has the info_documents migration been run?`
        return data.message
      }
      if (status === 401) return 'Your session expired. Please log in again.'
      if (status === 403) return 'You do not have permission to publish Info Center documents.'
      if (status === 404) return 'Endpoint not found. The API server may need to be restarted to pick up the new routes.'
      if (status === 419) return 'Session token expired. Refresh the page and try again.'
      return `${fallback} (HTTP ${status})`
    },
    cancel () {
      this.$router.push({ path: '/info-center' })
    },
    confirmDelete () {
      this.showDeleteConfirm = true
    },
    deleteDocument () {
      if (!this.docId) return
      axios.delete(`${this.$apiAdress}/api/info-documents/${this.docId}`)
        .then(() => {
          this.$router.push({ path: '/info-center' })
        })
        .catch((error) => {
          this.errorMessage = this.formatRequestError(error, 'Unable to delete document.')
        })
        .finally(() => {
          this.showDeleteConfirm = false
        })
    }
  }
}
</script>

<style scoped>
/* ─── Page shell ──────────────────────────────────────────────────── */
/* The route is mounted with meta.fullscreen=true, so the global
   .c-app--fullscreen rule already removes header + container padding.
   We just need to fill the viewport without any breakout margins,
   otherwise the page extends past the screen and scrolls horizontally. */
.ie-wrap {
  background: #f1f4f8;
  width: 100%;
  margin: 0;
  padding: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.ie-wrap--loading { background: #ffffff; }

/* ─── App bar ─────────────────────────────────────────────────────── */
.ie-appbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 1.1rem;
  background: #ffffff;
  border-bottom: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.ie-appbar__left { display: flex; align-items: center; gap: 0.65rem; min-width: 0; }
.ie-appbar__right { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }

.ie-appbar__name { display: flex; flex-direction: column; min-width: 0; }
.ie-appbar__label {
  font-size: 0.62rem;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  line-height: 1;
}
.ie-appbar__title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #1f2a44;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50vw;
}

.ie-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}
.ie-status-chip .ie-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.05);
}
.ie-status-chip.is-draft     { background: #fef3c7; color: #92400e; }
.ie-status-chip.is-draft .ie-status-dot { background: #f59e0b; }
.ie-status-chip.is-published { background: #dff5ea; color: #0e5b46; }
.ie-status-chip.is-published .ie-status-dot { background: #16a07a; }
.ie-status-chip.is-archived  { background: #f1f5f9; color: #475569; }
.ie-status-chip.is-archived .ie-status-dot { background: #64748b; }

/* ─── Buttons ─────────────────────────────────────────────────────── */
.ie-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: #475569;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.ie-icon-btn:hover { background: #f1f5f9; color: #0e5b46; }
.ie-icon-btn:focus { outline: none; }
.ie-icon-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.ie-icon-btn--close { color: #64748b; }
.ie-icon-btn--close:hover { background: #fef2f2; color: #b91c1c; }

.ie-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 0.95rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.ie-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}
.ie-btn--primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28); }
.ie-btn--primary:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.ie-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
}
.ie-btn--ghost:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.ie-btn--ghost:disabled { opacity: 0.55; cursor: not-allowed; }
.ie-btn--danger {
  background: #ffffff;
  border-color: #fecaca;
  color: #b91c1c;
}
.ie-btn--danger:hover { background: #fef2f2; border-color: #ef4444; color: #991b1b; }
.ie-btn:focus { outline: none; }
.ie-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.ie-btn--danger:focus-visible { box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30); }

.ie-alert {
  margin: 0.75rem 1.1rem 0;
  border-radius: 10px;
}

.ie-loading { padding: 3rem; text-align: center; color: #64748b; flex: 1; }

/* ─── Word-like canvas ────────────────────────────────────────────── */
.ie-canvas {
  flex: 1;
  background: #f1f4f8;
  padding: 1.5rem 1.1rem 3rem;
  /* No internal scroll — the page scrolls as one unit so .ie-appbar
     and .ie-ribbon stay sticky against the outer viewport. */
}

.ie-page {
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 4px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.06),
    0 18px 48px rgba(15, 23, 42, 0.10);
  padding: 3.5rem 4rem;
  min-height: calc(100vh - 200px);
}

@media (max-width: 720px) {
  .ie-page { padding: 2rem 1.25rem; border-radius: 0; }
}

.ie-canvas__footer {
  max-width: 860px;
  margin: 1rem auto 0;
  display: flex;
  justify-content: flex-end;
}

.ie-doc-title {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0 0 0.25rem;
  font-size: 2.2rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.15;
  letter-spacing: -0.02em;
}
.ie-doc-title::placeholder { color: #cbd5e1; font-weight: 600; }
.ie-doc-title:focus { outline: none; }

.ie-doc-summary {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0 0 0.85rem;
  font-size: 1rem;
  color: #475569;
  line-height: 1.55;
}
.ie-doc-summary::placeholder { color: #cbd5e1; }
.ie-doc-summary:focus { outline: none; }

.ie-doc-meta {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.ie-doc-meta__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.18rem 0.7rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}
.ie-doc-meta__chip--soft {
  background: #f1f5f9;
  color: #475569;
  font-weight: 600;
}
.ie-doc-meta__chip--pin {
  background: #fef3c7;
  color: #92400e;
}

.ie-pill--green  { background: #dff5ea; color: #0e5b46; }
.ie-pill--blue   { background: #dbeafe; color: #1e40af; }
.ie-pill--amber  { background: #fef3c7; color: #92400e; }
.ie-pill--violet { background: #ede9fe; color: #5b21b6; }
.ie-pill--teal   { background: #ccfbf1; color: #115e59; }
.ie-pill--pink   { background: #fce7f3; color: #9d174d; }
.ie-pill--red    { background: #fee2e2; color: #991b1b; }
.ie-pill--cyan   { background: #cffafe; color: #155e75; }
.ie-pill--gray   { background: #f1f5f9; color: #475569; }

.ie-doc-divider {
  border: 0;
  height: 1px;
  background: #eef1f7;
  margin: 0.5rem 0 1.25rem;
}

/* The toolbar lives in the full-width ribbon below the app bar. The
   editor inside .ie-page is just the content surface — flatten its
   wrapper so it blends into the page. */
.ie-page >>> .rt-editor {
  border: none;
  box-shadow: none;
  border-radius: 0;
}
.ie-page >>> .rt-content .ProseMirror {
  min-height: 50vh;
  font-size: 1rem;
  line-height: 1.8;
}

/* ─── Full-width ribbon (RichTextToolbar lives here) ─────────────── */
.ie-ribbon {
  position: sticky;
  /* App bar height ≈ 0.55rem padding × 2 + 34px button = ~52px. */
  top: 52px;
  z-index: 25;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid #e5e9f2;
  box-shadow: 0 4px 12px -8px rgba(15, 23, 42, 0.18);
}

.ie-ribbon__inner {
  display: flex;
  align-items: center;
  padding: 0.45rem 1.1rem;
  min-height: 48px;
}

/* ─── Publish modal ──────────────────────────────────────────────── */
.ie-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.ie-modal-fade-enter-active, .ie-modal-fade-leave-active { transition: opacity 0.18s ease; }
.ie-modal-fade-enter-active .ie-modal__panel, .ie-modal-fade-leave-active .ie-modal__panel { transition: transform 0.22s ease, opacity 0.18s ease; }
.ie-modal-fade-enter, .ie-modal-fade-leave-to { opacity: 0; }
.ie-modal-fade-enter .ie-modal__panel, .ie-modal-fade-leave-to .ie-modal__panel { opacity: 0; transform: translateY(8px) scale(0.98); }

.ie-modal__panel {
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}

.ie-modal__head {
  padding: 1.1rem 1.25rem 0.85rem;
  border-bottom: 1px solid #eef1f7;
  background:
    radial-gradient(800px 200px at 0% 0%, rgba(22, 160, 122, 0.10), transparent 60%),
    radial-gradient(700px 220px at 100% 0%, rgba(14, 91, 70, 0.08), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, #f8fffb 100%);
}
.ie-modal__head-row {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}
.ie-modal__icon-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 11px;
  color: #ffffff;
  font-size: 1.25rem;
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.22);
  flex-shrink: 0;
}
.ie-modal__head-text {
  flex: 1 1 auto;
  min-width: 0;
}
.ie-modal__head h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.ie-modal__head p {
  margin: 0.2rem 0 0;
  font-size: 0.82rem;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ie-modal__progress {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.85rem;
  flex-wrap: wrap;
}
.ie-modal__progress-step {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ie-modal__progress-step.is-done   { color: #0e5b46; }
.ie-modal__progress-step.is-current{ color: #0f172a; }
.ie-modal__progress-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #cbd5e1;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.05);
}
.ie-modal__progress-step.is-done .ie-modal__progress-dot   { background: #16a07a; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18); }
.ie-modal__progress-step.is-current .ie-modal__progress-dot{ background: #0f172a; box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.12); }
.ie-modal__progress-line {
  flex: 1 1 18px;
  height: 1px;
  background: #e5e9f2;
  min-width: 12px;
}

.ie-modal__h-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.ie-modal__h-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
}
.ie-modal__h-meta {
  margin-left: auto;
  font-size: 0.72rem;
  color: #64748b;
  font-weight: 600;
  text-transform: capitalize;
}

.ie-aud-row--master {
  background: #f8fafc;
  border: 1px dashed #e5e9f2;
}
.ie-aud-row--master:hover { background: #f1f5f9; }

.ie-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.1rem 1.25rem;
  background: #fbfcfe;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ie-modal__section { display: flex; flex-direction: column; gap: 0.5rem; }
.ie-modal__section--inline { flex-direction: row; align-items: center; flex-wrap: wrap; gap: 0.85rem; }

.ie-modal__h {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
.ie-modal__h--inline { text-transform: none; letter-spacing: 0; font-size: 0.8rem; color: #1f2a44; font-weight: 600; }

.ie-modal__hint {
  margin: 0;
  font-size: 0.76rem;
  color: #94a3b8;
  line-height: 1.45;
}

.ie-cat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.4rem;
}
@media (max-width: 540px) {
  .ie-cat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.ie-cat-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.55rem 0.35rem;
  border-radius: 10px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease, transform 0.1s ease;
}
.ie-cat-btn__icon { font-size: 1.1rem; line-height: 1; }
.ie-cat-btn__label { font-size: 0.72rem; font-weight: 600; }
.ie-cat-btn:hover { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.ie-cat-btn.is-active {
  border-color: transparent;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.22);
  transform: translateY(-1px);
}
.ie-cat-btn:focus { outline: none; }
.ie-cat-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.ie-checklist {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.15rem 0.5rem;
  max-height: 220px;
  overflow-y: auto;
  padding: 0.1rem;
}
@media (max-width: 540px) {
  .ie-checklist { grid-template-columns: 1fr; }
}

.ie-aud-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  margin: 0;
  user-select: none;
  transition: background 0.12s ease;
}
.ie-aud-row:hover { background: #f1f5f9; }
/* `is-muted` rows are still clickable — they're just visually quieter
   while the master ("Everyone" / "All organisations") is on. Clicking
   one auto-flips out of the master state. */
.ie-aud-row.is-muted { opacity: 0.55; }
.ie-aud-row.is-muted:hover { opacity: 1; background: #f1f5f9; }
.ie-aud-row--inline { flex: 1 1 auto; }

.ie-aud-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #16a07a;
  cursor: pointer;
}
.ie-aud-label {
  font-size: 0.82rem;
  color: #1f2a44;
  font-weight: 500;
  text-transform: capitalize;
}

.ie-inline-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ie-modal__icon-input {
  width: 64px;
  height: 32px;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  padding: 0 0.55rem;
  text-align: center;
  font-size: 1rem;
}
.ie-modal__icon-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.ie-modal__foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  background: #ffffff;
  border-top: 1px solid #eef1f7;
  flex-wrap: wrap;
}
.ie-modal__ready {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 600;
}
.ie-modal__ready-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.18);
  transition: background 0.18s ease, box-shadow 0.18s ease;
}
.ie-modal__ready-dot.is-ok {
  background: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.ie-modal__foot-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* ─── Success overlay ────────────────────────────────────────────── */
.ie-success-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: radial-gradient(circle at center, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.78) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.ie-success-fade-enter-active, .ie-success-fade-leave-active {
  transition: opacity 0.25s ease;
}
.ie-success-fade-enter, .ie-success-fade-leave-to { opacity: 0; }

.ie-success-card {
  background: #ffffff;
  padding: 1.75rem 2.5rem 1.85rem;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32);
  text-align: center;
  animation: ie-success-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.ie-success-card h2 {
  margin: 0.9rem 0 0.3rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
}

.ie-success-card p {
  margin: 0;
  font-size: 0.85rem;
  color: #475569;
  max-width: 280px;
  line-height: 1.5;
}

@keyframes ie-success-pop {
  0%   { transform: scale(0.7); opacity: 0; }
  60%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); }
}

.ie-success-check { width: 84px; height: 84px; display: block; margin: 0 auto; }

.ie-success-check__ring {
  fill: #f0faf5;
  stroke: #16a07a;
  stroke-width: 3;
  stroke-dasharray: 215;
  stroke-dashoffset: 215;
  transform-origin: 50% 50%;
  animation: ie-ring-draw 0.55s ease-out forwards;
}

.ie-success-check__tick {
  fill: none;
  stroke: #0e5b46;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: ie-tick-draw 0.35s 0.45s ease-out forwards;
}

@keyframes ie-ring-draw {
  to { stroke-dashoffset: 0; }
}

@keyframes ie-tick-draw {
  to { stroke-dashoffset: 0; }
}
</style>
